/**
 * Epic 14 — Reporting and data: time, reports, goals, import and export.
 * Every number comes from the server, counted over tasks the viewer can open.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { API_CALENDAR_PREFIX, SuiteApiClient, fetchWithAuth } from "@weco/suite-client";
import { call, type TaskCard } from "../task/api";

const client = new SuiteApiClient(API_CALENDAR_PREFIX);

export interface TaskBriefRef {
  id: string;
  key: string;
  title: string;
}
export interface Timer {
  task: TaskBriefRef;
  started_at: string;
}
export interface TimeEntry {
  id: string;
  email: string;
  name: string;
  minutes: number;
  started_at: string;
  note: string;
  source: "timer" | "manual";
  edited: boolean;
  mine: boolean;
}
export interface TaskTime {
  entries: TimeEntry[];
  total: number;
  estimate: number | null;
  subtask_estimate: number;
  can_log: boolean;
}
export interface ReportData {
  total: number;
  truncated: boolean;
  by_assignee: { email: string | null; name: string | null; ids: string[] }[];
  overdue: string[];
  by_status: { key: string; name: string; color: string; category: string; ids: string[] }[];
  weekly: { start: string; ids: string[] }[];
  time: { id: string; key: string; title: string; estimate: number | null; logged: number; assignee: string | null; squad_name: string }[];
  tasks: Record<string, TaskCard>;
}
export type GoalTarget = "tasks" | "percent" | "number";
export type GoalStatus = "on_track" | "at_risk" | "off_track";
export interface Goal {
  id: string;
  hub: string;
  name: string;
  description: string;
  target_type: GoalTarget;
  target_value: number;
  current_value: number;
  progress: number;
  linked_count: number;
  done_count: number;
  hidden_count: number;
  tasks: (TaskBriefRef & { done: boolean })[];
  owner: { email: string; name: string };
  due_date: string | null;
  status: GoalStatus;
  can_edit: boolean;
}
export type GoalBody = Partial<Pick<Goal, "name" | "description" | "target_type" | "target_value" | "current_value" | "due_date" | "status">> & { owner?: string };
export interface ImportRow {
  line: number;
  title: string;
  description?: string;
  status?: string;
  priority?: string;
  assignees?: string[];
  due_date?: string;
  labels?: string[];
  estimate?: number | null;
}
export interface ImportResult {
  id?: string;
  report: { line: number; title: string; result: "imported" | "skipped" | "failed"; reason: string }[];
  imported?: number;
  skipped?: number;
  failed?: number;
  duplicate_of?: string | null;
}

/** US 14.4 — direct import from another tool. Credentials go with each call and are never saved. */
export type DirectProvider = "jira" | "clickup" | "trello" | "planner";
export type DirectCredentials = Record<string, string>;
export interface DirectReportLine {
  line: number;
  key: string;
  title: string;
  url: string;
  result: "imported" | "skipped" | "failed";
  reason: string;
}
export interface DirectPreview {
  project_name: string;
  report: DirectReportLine[];
  preview: (DirectReportLine & { status_name: string; assignees: string[]; due_date: string })[];
  duplicate_of: string | null;
  truncated: boolean;
}
export interface DirectResult {
  id?: string;
  project_name?: string;
  report: DirectReportLine[];
  imported?: number;
  skipped?: number;
  failed?: number;
  truncated?: boolean;
}
type DirectBody = { provider: DirectProvider; credentials: DirectCredentials; project: string; squad: string; confirm_duplicate?: boolean; time_zone?: string };

export const directImportApi = {
  projects: (provider: DirectProvider, credentials: DirectCredentials) =>
    call(() => client.post<{ results: { id: string; name: string }[] }>("/task-imports/direct/projects/", { provider, credentials })).then((r) => r.results),
  preview: (body: DirectBody) => call(() => client.post<DirectPreview>("/task-imports/direct/", { ...body, dry_run: true })),
  run: (body: DirectBody) => call(() => client.post<DirectResult>("/task-imports/direct/", body)),
};

export const reportingApi = {
  timer: () => call(() => client.get<{ timer: Timer | null }>("/timer/")),
  start: (task: string) => call(() => client.post<{ timer: Timer; stopped: { task: TaskBriefRef; minutes: number } | null }>("/timer/", { task })),
  stop: () => call(() => client.delete<{ minutes: number; task: TaskBriefRef; entry: TimeEntry | null }>("/timer/")),
  taskTime: (task: string) => call(() => client.get<TaskTime>(`/hub-tasks/${task}/time/`)),
  addTime: (task: string, body: { minutes: number; date?: string; note?: string }) => call(() => client.post<TimeEntry>(`/hub-tasks/${task}/time/`, body)),
  editTime: (id: string, body: { minutes?: number; date?: string; note?: string }) => call(() => client.patch<TimeEntry>(`/time-entries/${id}/`, body)),
  deleteTime: (id: string) => call(() => client.delete<void>(`/time-entries/${id}/`)),
  reports: (scope: { hub?: string; squad?: string; filters?: Record<string, unknown> }) => {
    const p = new URLSearchParams();
    if (scope.squad) p.set("squad", scope.squad);
    else if (scope.hub) p.set("hub", scope.hub);
    if (scope.filters && Object.keys(scope.filters).length) p.set("filters", JSON.stringify(scope.filters));
    return call(() => client.get<ReportData>(`/task-reports/?${p.toString()}`));
  },
  goals: (hub: string) => call(() => client.get<{ results: Goal[] }>(`/hubs/${hub}/goals/`)).then((r) => r.results),
  addGoal: (hub: string, body: GoalBody) => call(() => client.post<Goal>(`/hubs/${hub}/goals/`, body)),
  editGoal: (id: string, body: GoalBody) => call(() => client.patch<Goal>(`/goals/${id}/`, body)),
  deleteGoal: (id: string) => call(() => client.delete<void>(`/goals/${id}/`)),
  linkTask: (goal: string, task: string) => call(() => client.post<Goal>(`/goals/${goal}/tasks/`, { task })),
  unlinkTask: (goal: string, task: string) => call(() => client.delete<Goal>(`/goals/${goal}/tasks/${task}/`)),
  importRows: (body: { squad: string; file_name: string; rows: ImportRow[]; dry_run?: boolean; confirm_duplicate?: boolean; time_zone?: string }) =>
    call(() => client.post<ImportResult>("/task-imports/", body)),
};

/** A CSV from the server: downloaded here, or (large exports) emailed. */
export async function downloadCsv(path: string, body: unknown, fallbackName: string): Promise<{ emailed: boolean; rows?: number }> {
  const resp = await fetchWithAuth(`${API_CALENDAR_PREFIX}${path}`, {
    method: path.includes("?") || body === undefined ? "GET" : "POST",
    headers: body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (resp.status === 202) {
    const data = (await resp.json()) as { rows: number };
    return { emailed: true, rows: data.rows };
  }
  if (!resp.ok) {
    const data = (await resp.json().catch(() => ({}))) as { detail?: string };
    throw new Error(data.detail || String(resp.status));
  }
  const blob = await resp.blob();
  const name = /filename="([^"]+)"/.exec(resp.headers.get("Content-Disposition") || "")?.[1] || fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return { emailed: false };
}

export const TIMER_KEY = ["tasks", "timer"] as const;

/** My running timer — shared by task detail and the top bar. */
export function useTimer() {
  return useQuery({ queryKey: TIMER_KEY, queryFn: () => reportingApi.timer().then((r) => r.timer), refetchInterval: 30_000, refetchOnWindowFocus: true, retry: false });
}

export function useRefreshTime() {
  const qc = useQueryClient();
  return (taskId?: string) => {
    void qc.invalidateQueries({ queryKey: TIMER_KEY });
    if (taskId) void qc.invalidateQueries({ queryKey: ["tasks", "task-time", taskId] });
    void qc.invalidateQueries({ queryKey: ["tasks", "reports"] });
  };
}

// ── Durations ──────────────────────────────────────────────────────────────

/** "1h 30m", "90", "1:30", "1.5h" → minutes; NaN when it isn't a duration. */
export function parseDuration(v: string): number {
  const s = String(v ?? "").trim().toLowerCase();
  if (!s) return NaN;
  let m = /^(\d+):([0-5]?\d)$/.exec(s);
  if (m) return +m[1] * 60 + +m[2];
  if (/^\d+$/.test(s)) return +s;
  m = /^(?:(\d+(?:\.\d+)?)\s*h)?\s*(?:(\d+)\s*m)?$/.exec(s);
  if (m && (m[1] || m[2])) return Math.round((m[1] ? parseFloat(m[1]) * 60 : 0) + (m[2] ? +m[2] : 0));
  return NaN;
}

export function formatDuration(min: number | null | undefined): string {
  if (min == null || Number.isNaN(min)) return "—";
  const a = Math.abs(Math.round(min));
  const h = Math.floor(a / 60);
  const m = a % 60;
  return (min < 0 ? "−" : "") + (h ? `${h}h ${m}m` : `${m}m`);
}
